/* eslint-disable max-statements */

import { Box, Divider, Group, LoadingOverlay, Tabs } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { EnvironmentCountries, LowValuesDeclarantDataSource } from '@e-origin/shared';

import { anomalyValueIcon } from '@assets';
import { BackButton, TextInput } from '@components';
import { CustomerViewTabs } from '@enums';
import { ContentLayout } from '@features/layout';
import { useCustomer } from '@hooks/customer';
import { useDeclarant } from '@hooks/declarant';
import { useSettings } from '@hooks/useSettings';
import { CustomerViewApiUser } from './CustomerViewApiUser';
import { CustomerViewDeclarant } from './CustomerViewDeclarant';
import { CustomerViewEvaluationMethods } from './CustomerViewEvaluationMethods';
import { CustomerViewImportGuarantees } from './CustomerViewImportGuarantees';
import { CustomerViewPayments } from './CustomerViewPayments';
import { CustomerViewRepresentative } from './CustomerViewRepresentative';
import { CustomerViewTransitAuth } from './CustomerViewTransitAuth';
import { CustomerViewTransitGuarantees } from './CustomerViewTransitGuarantees';
import { useCustomerHandlers } from './useCustomerHandlers';
import { useCustomerViewFormInitializer } from './useCustomerViewFormInitializer';

const EVALUATION_METHODS_TAB = 'evaluation-methods';

export const CustomerView = () => {
  const { id, tabId } = useParams();
  const navigate = useNavigate();
  const { data: settings } = useSettings();
  const isBE = settings.country === EnvironmentCountries.BE;
  const isNL = settings.country === EnvironmentCountries.NL;

  const basePath = `/customers/${id || 'add'}`;
  const isCreate = !id;

  const { data: customerDetails, isLoading } = useCustomer(id);
  const { data: generalDeclarant } = useDeclarant();

  const [tabsValidity, setTabsValidity] = useState({
    [CustomerViewTabs.EVALUATION_METHODS]: true,
  });

  const { form, buildFormValues } = useCustomerViewFormInitializer({ generalDeclarant, customerDetails });

  useEffect(() => {
    if (generalDeclarant && customerDetails) {
      form.setValues(buildFormValues(generalDeclarant, customerDetails));
    }
  }, [generalDeclarant, customerDetails]);

  const { handleSubmitCustomer, handleUpdatePayments } = useCustomerHandlers(form, id, isCreate, generalDeclarant, customerDetails);

  useEffect(() => {
    if (!tabId) {
      navigate(`${basePath}/${settings?.isForwarder ? EVALUATION_METHODS_TAB : 'declarant'}`);
    } else if (settings?.isForwarder && tabId !== EVALUATION_METHODS_TAB) {
      navigate(`${basePath}/${EVALUATION_METHODS_TAB}`);
    }
  }, [tabId, navigate]);

  const handleTabChange = (value: string) => {
    navigate(`${basePath}/${value}`);
  };

  const handleTabValidityChanged = (tab, isValid) => {
    setTabsValidity({ ...tabsValidity, [tab]: isValid });
  };

  const isButtonDisabled = !form.values.name;

  if (!isCreate && !customerDetails) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  const hvSuffix = generalDeclarant?.functionalFeatures?.lowValuesDeclarantDataSource === LowValuesDeclarantDataSource.CUSTOMER ? '' : ' (HV)';

  return (
    <ContentLayout>
      <Group>
        <Box ml="xl">
          <BackButton backPath="/customers" />
        </Box>
        <TextInput {...form.getInputProps('name')} label="Customer name" value={form.values.name} ml="xl" />
      </Group>
      <Divider mt="md" />

      <ContentLayout.Content>
        <Tabs
          h="100%"
          defaultValue={settings?.isForwarder ? 'evaluation-methods' : 'declarant'}
          value={tabId}
          onChange={handleTabChange}
          orientation="vertical"
          keepMounted={false}
          py="lg"
        >
          <Tabs.List>
            {!settings?.isForwarder && (
              <>
                <Tabs.Tab value="declarant">Declarant{hvSuffix}</Tabs.Tab>
                <Tabs.Tab value="representative">Representative{hvSuffix}</Tabs.Tab>
                <Tabs.Tab value="payments">Payments</Tabs.Tab>
              </>
            )}
            <Tabs.Tab
              value={EVALUATION_METHODS_TAB}
              leftSection={!tabsValidity[CustomerViewTabs.EVALUATION_METHODS] && <img src={anomalyValueIcon} alt="" style={{ minWidth: 16 }} />}
            >
              Evaluation Methods
            </Tabs.Tab>
            {!settings?.isForwarder && (
              <>
                <Tabs.Tab value="guarantees-for-import">Guarantees for Import</Tabs.Tab>
                <Tabs.Tab value="api-user">API User</Tabs.Tab>
                <Tabs.Tab value="transit-authorizations">Transit authorizations</Tabs.Tab>
                <Tabs.Tab value="guarantees-for-transit">Guarantees for transit</Tabs.Tab>
              </>
            )}
          </Tabs.List>

          {!settings?.isForwarder && (
            <>
              <Tabs.Panel value="declarant">
                <CustomerViewDeclarant
                  handleSubmitCustomer={handleSubmitCustomer}
                  isButtonDisabled={isButtonDisabled}
                  customerDetails={customerDetails}
                  isCreate={isCreate}
                  isBE={isBE}
                />
              </Tabs.Panel>
              <Tabs.Panel value="representative">
                <CustomerViewRepresentative
                  handleSubmitCustomer={handleSubmitCustomer}
                  customerDetails={customerDetails}
                  isButtonDisabled={isButtonDisabled}
                  isCreate={isCreate}
                  isBE={isBE}
                />
              </Tabs.Panel>
              <Tabs.Panel value="payments">
                <CustomerViewPayments
                  handleSubmitCustomer={handleSubmitCustomer}
                  handleUpdatePayments={handleUpdatePayments}
                  isButtonDisabled={isButtonDisabled}
                  customerDetails={customerDetails}
                  isCreate={isCreate}
                  isBE={isBE}
                  isNL={isNL}
                />
              </Tabs.Panel>
              <Tabs.Panel value="guarantees-for-import">
                <CustomerViewImportGuarantees
                  handleSubmitCustomer={handleSubmitCustomer}
                  isButtonDisabled={isButtonDisabled}
                  language={generalDeclarant.language}
                  customerDetails={customerDetails}
                  isCreate={isCreate}
                />
              </Tabs.Panel>
              <Tabs.Panel value="api-user">
                <CustomerViewApiUser customerDetails={customerDetails} handleSubmitCustomer={handleSubmitCustomer} isCreate={isCreate} customerId={id} />
              </Tabs.Panel>
              <Tabs.Panel value="transit-authorizations">
                <CustomerViewTransitAuth customerDetails={customerDetails} handleSubmitCustomer={handleSubmitCustomer} isCreate={isCreate} />
              </Tabs.Panel>
              <Tabs.Panel value="guarantees-for-transit">
                <CustomerViewTransitGuarantees
                  customerDetails={customerDetails}
                  handleSubmitCustomer={handleSubmitCustomer}
                  isButtonDisabled={isButtonDisabled}
                  isCreate={isCreate}
                />
              </Tabs.Panel>
            </>
          )}
          <Tabs.Panel value={EVALUATION_METHODS_TAB}>
            <CustomerViewEvaluationMethods
              customerDetails={customerDetails}
              handleSubmitCustomer={handleSubmitCustomer}
              isButtonDisabled={isButtonDisabled}
              isCreate={isCreate}
              handleTabValidityChanged={handleTabValidityChanged}
            />
          </Tabs.Panel>
        </Tabs>
      </ContentLayout.Content>
    </ContentLayout>
  );
};
